import type { Candidate } from '@/model/Candidate/Candidate';
import { CandidateHistoryAction } from '@/services/candidates/types';
import { useRJActivityLogsQuery } from '@/shared/legacy/hooks/useRJActivityLogsQuery';
import { usePageContext } from 'hooks/shared/usePageContext';
import { PageContext } from 'hooks/shared/usePageContext.types';
import { SelectAtsVacancyModalType } from '@/shared/legacy/contexts/DialogContext/modals/SelectAtsVacancyForCampaignModal/SelectAtsVacancyForCampaignModal.types';
import { useOpenWithAtsVacancyPrompt } from '@/shared/legacy/contexts/DialogContext/modals/SelectAtsVacancyForCampaignModal/useOpenWithAtsVacancyPrompt';
import { useCallback, useContext, useMemo, useRef, useState } from 'react';
import {
  ConfirmUnlockIntentionResolver,
  UseActionsHook,
  UseActionsHookReturnType,
  UseActionsIsLoadingState,
} from 'shared/legacy/contexts/CandidateContext/types';
import { LocalizationContext } from 'shared/legacy/contexts/LocalizationContext/LocalizationContext';
import { CanceledByUserException } from 'shared/legacy/exceptions';
import { useCampaignStore } from 'stores/campaign/campaign.store';
import { getAvailableActions } from 'utils/candidates';
import { getAvailableActionsForInbox } from 'utils/candidates/availableCandidateActions';
import { useContact } from './useContact';
import { useHidden } from './useHidden';
import { useMakeInvisible } from './useMakeInvisible';
import { useMatch } from './useMatch';
import { useRefresh } from './useRefresh';
import { useResendAts } from './useResendAts';
import { useRJComm } from './useRJComm';
import { useSaveLabels } from './useSaveLabels';
import { useSetActiveJobBoard } from './useSetActiveJobBoard';
import { useShare } from './useShare';
import { useShortlist } from './useShortlist';
import { useUnlock } from './useUnlock';

export const useActions: UseActionsHook = (candidate, { shouldConfirmUnlockIntention = false }) => {
  const page = usePageContext();
  const { dictionary } = useContext(LocalizationContext);
  const campaign = useCampaignStore((s) => s.campaign);
  const isLastAutomationAttemptAccepted = useLastAutomationAttemptAccepted(candidate, campaign?.id);
  const candidateActions =
    page === PageContext.CentralInbox
      ? getAvailableActionsForInbox()
      : getAvailableActions({
          shouldShowScheduleInterviewButton: isLastAutomationAttemptAccepted,
        });
  const [isLoading, _setIsLoading] = useState<UseActionsIsLoadingState>({
    share: false,
    unlock: false,
    saveLabels: false,
    approve: false,
    contact: false,
    makeInvisible: false,
    resendAts: false,
    reject: false,
    shortlist: false,
    refresh: false,
    setActiveJobBoard: false,
    rjComm: false,
  });
  const [confirmUnlockModalShouldBeVisible, setConfirmUnlockModalShouldBeVisible] = useState(false);
  const confirmUnlockIntentionResolverRef = useRef<ConfirmUnlockIntentionResolver>({
    resolve: () => undefined,
  });

  const availableActions = useMemo(() => {
    if (!candidate?.status || !candidateActions) {
      return {
        actions: [],
        extraOptions: {},
      };
    }

    return (
      candidateActions[candidate.status] ?? {
        actions: [],
        extraOptions: {},
      }
    );
  }, [candidateActions, candidate?.status, page]);

  const setIsLoading = useCallback((newLoadingState: Partial<UseActionsIsLoadingState>) => {
    _setIsLoading((oldLoadingState) => ({ ...oldLoadingState, ...newLoadingState }));
  }, []);

  const loadDependenciesFor = useCallback(
    (key: keyof UseActionsIsLoadingState, options?: { registerWerknlActivity?: boolean }) => ({
      candidate,
      setIsLoading: (isLoading: boolean) => setIsLoading({ [key]: isLoading }),
      registerWerknlActivity: options?.registerWerknlActivity,
    }),
    [candidate],
  );

  const setActiveJobBoard = useSetActiveJobBoard(loadDependenciesFor('setActiveJobBoard'));
  const refresh = useRefresh(loadDependenciesFor('refresh'));
  const unlock = useUnlock(loadDependenciesFor('unlock', { registerWerknlActivity: true }));
  const shortlist = useShortlist(loadDependenciesFor('shortlist', { registerWerknlActivity: true }));
  const contact = useContact(loadDependenciesFor('contact', { registerWerknlActivity: true }));
  const match = useMatch(loadDependenciesFor('approve', { registerWerknlActivity: true }));
  const hide = useHidden(loadDependenciesFor('reject', { registerWerknlActivity: true }));
  const makeInvisible = useMakeInvisible(loadDependenciesFor('makeInvisible'));
  const saveLabels = useSaveLabels(loadDependenciesFor('saveLabels', { registerWerknlActivity: true }));
  const share = useShare(loadDependenciesFor('share', { registerWerknlActivity: true }));
  const resendAts = useResendAts(loadDependenciesFor('resendAts', { registerWerknlActivity: true }));
  const rjComm = useRJComm(loadDependenciesFor('rjComm'));

  const confirmUnlockIntention = useCallback(async (): Promise<boolean> => {
    if (!shouldConfirmUnlockIntention) return Promise.resolve(true);

    setConfirmUnlockModalShouldBeVisible(true);
    return new Promise((res, rej) => {
      confirmUnlockIntentionResolverRef.current = {
        resolve: (v) => {
          setConfirmUnlockModalShouldBeVisible(false);

          if (!v) {
            rej(new CanceledByUserException());
          }

          res(v);
        },
      };
    });
  }, [candidate]);

  // resendAts;

  const onRefresh: Actions['onRefresh'] = async ({ requestDetail } = {}) => refresh({ requestDetail });

  const onUnlock: Actions['onUnlock'] = async (params) => {
    if (candidate?.isUnlocked) {
      return true;
    }

    await confirmUnlockIntention();
    /// TODO WERKZOEKEN: Remove this after release new jobboard
    if (candidate?.activeJobBoard === 'monsterboard_v2')
      // if (candidate?.activeJobBoard === 'monsterboard_v2' || candidate?.activeJobBoard === 'werkzoeken')
      await onRefresh({ requestDetail: true });
    await unlock(params);

    return true;
  };

  const onSetActiveJobBoard: Actions['onSetActiveJobBoard'] = async (jobBoardId) => setActiveJobBoard({ jobBoardId });
  const onSaveLabels: Actions['onSaveLabels'] = async (labels, options) => saveLabels({ labels, options });
  const onShortlist: Actions['onShortlist'] = async (campaignId) => shortlist({ campaignId });
  const onContact: Actions['onContact'] = async (data, type, extras, options) =>
    contact({ data, sendMethod: type, extras, options });

  const openWithAtsPromptForMatch = useOpenWithAtsVacancyPrompt({
    openAction: (campaignId) => match({ campaignId }),
    candidateFirstName: candidate?.firstName || dictionary.theCandidate,
    type: SelectAtsVacancyModalType.Matching,
  });

  const onApprove: Actions['onApprove'] = async (campaignId) => openWithAtsPromptForMatch(campaignId);
  const onReject: Actions['onReject'] = async (labels, status_change_feedback) =>
    hide({ reasons: labels, status_change_feedback });
  const onMakeInvisible: Actions['onMakeInvisible'] = async (params) => makeInvisible(params);
  const onShare: Actions['onShare'] = async () => share();
  const onResendAts: Actions['onResendAts'] = async () => resendAts();
  const onRJComm: Actions['onRJComm'] = async (data) => rjComm({ data });

  return useMemo(
    () => ({
      isLoading,
      onShare,
      onUnlock,
      onSaveLabels,
      onApprove,
      onContact,
      onMakeInvisible,
      onResendAts,
      onReject,
      onShortlist,
      onRefresh,
      onSetActiveJobBoard,
      onRJComm,

      confirmUnlockModalShouldBeVisible,
      confirmUnlockIntentionResolverRef,

      availableActions,
    }),
    [candidate, isLoading, confirmUnlockModalShouldBeVisible, availableActions],
  );
};

export const isLoadingInitialState: UseActionsIsLoadingState = {
  share: false,
  unlock: false,
  saveLabels: false,
  approve: false,
  contact: false,
  makeInvisible: false,
  resendAts: false,
  reject: false,
  shortlist: false,
  refresh: false,
  setActiveJobBoard: false,
  rjComm: false,
};

type Actions = UseActionsHookReturnType;

const useLastAutomationAttemptAccepted = (candidate: Candidate | undefined, campaignId: number | undefined) => {
  const rjActivityLogsQuery = useRJActivityLogsQuery({
    es_person_id: candidate?.es_person_id,
    campaign_id: campaignId,
  });

  const isLastAutomationAttemptAccepted = useMemo(() => {
    if (!candidate || !campaignId) {
      return false;
    }

    if (!rjActivityLogsQuery.data?.history || !rjActivityLogsQuery.data?.job_improved_id) {
      return false;
    }

    const jobHistory = rjActivityLogsQuery.data.history[rjActivityLogsQuery.data.job_improved_id] as any[];
    if (!jobHistory || !Array.isArray(jobHistory) || jobHistory.length === 0) {
      return false;
    }

    const lastItem = jobHistory.at(-1);
    const lastAutomationAttempt = lastItem?.metadata?.automation_attempt;

    if (lastAutomationAttempt === undefined) {
      return false;
    }

    const acceptedAction = jobHistory.find(
      (step: any) => step.action === CandidateHistoryAction.RJCommHubCandidateAccepted,
    );

    if (!acceptedAction) {
      return false;
    }

    return acceptedAction.metadata?.automation_attempt === lastAutomationAttempt;
  }, [candidate, campaignId, rjActivityLogsQuery.data?.history, rjActivityLogsQuery.data?.job_improved_id]);

  return isLastAutomationAttemptAccepted;
};
